import {
  createAsyncThunk,
} from '@reduxjs/toolkit';

import type { ThunkApiType, voidFunctionType } from '@/store';
import type { PageJson } from '@/types/domain/childNotebook/entities/pageEntity';
import type { DefecationJson } from '@/types/domain/defecation/entities/defecationEntity';
import type { DrugJson } from '@/types/domain/drug/entities/drugEntity';
import type { MealJson } from '@/types/domain/meal/entities/mealEntity';
import type { MilkJson } from '@/types/domain/milk/entities/milkEntity';
import type { SleepJson } from '@/types/domain/sleep/entities/sleepEntity';
import type { TemperatureJson } from '@/types/domain/temperature/entities/temperatureEntity';

import { childNotebookSlice } from '@/domain/childNotebook/redux';
import { uiSlice } from '@/domain/ui/redux';
import { service } from '@/share';
import { newDateFromString, toStringDate } from '@/utils/date';

export const refreshPage = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'childNotebook/refreshPage',
  async (_, thunkApi) => {
    const { date } = thunkApi.getState().childNotebook;
    const items = await service.childNotebook.getPageList(date);
    thunkApi.dispatch(childNotebookSlice.actions.addPages({
      items,
      date,
    }));
  },
);

const execRetractPage = async (data: PageJson, facilityIds: string[]) => {
  if (facilityIds.length > 0) {
    const promises = facilityIds.map(
      (v) => service.childNotebook.removeNurseryPage(
        {
          data: data.data,
          pub: {
            sendId: data.pub.sendId,
            replyId: v,
            data: { ...data.pub.data },
          },
        },
      ),
    );
    await Promise.all(promises);
  }
  const page = JSON.parse(JSON.stringify(data));
  page.data.isSent = false;
  page.data.images = [];
  page.data.attachments = [];
  page.pub.data.alreadyRead = false;
  await service.childNotebook.createPage(page);
};

/**
 * ページを送信する
 */
export const sendPage = createAsyncThunk<
  void,
  {
    /** データ */
    data: PageJson,
    /** ファイル */
    attachments: File[],
    /** 施設IDリスト */
    facilityIds: string[],
    /** 処理後アクション */
    action: voidFunctionType,
  },
  ThunkApiType
>(
  'childNotebook/sendPage',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      let isOk = true;
      const {
        page,
        images,
        attachments,
      } = await service.childNotebook.extractFiles(input.data, input.attachments);
      if (input.facilityIds.length > 0) {
        const promises = input.facilityIds.map(
          (v) => service.childNotebook.sendNurseryPage(
            {
              data: page.data,
              pub: {
                sendId: page.pub.sendId,
                replyId: v,
                data: { ...page.pub.data },
              },
            },
            images,
            attachments,
          ),
        );
        const result = await Promise.all(promises);
        if (result.includes(false)) {
          isOk = false;
        }
      }
      if (isOk) {
        thunkApi.dispatch(uiSlice.actions.setMessage({
          key: 'info',
          content: '送信しました',
        }));
        input.action();
      } else {
        // ロールバック
        await execRetractPage(input.data, input.facilityIds);
        thunkApi.dispatch(uiSlice.actions.setMessage({
          key: 'error',
          content: '失敗しました',
        }));
      }
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

/**
 * ページの送信取り消し
 */
export const retractPage = createAsyncThunk<
  void,
  {
    /** データ */
    data: PageJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'childNotebook/retractPage',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      await execRetractPage(input.data, input.facilityIds);
      thunkApi.dispatch(uiSlice.actions.setMessage({
        key: 'info',
        content: '取り消ししました',
      }));
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

/**
 * 下書きページを保存する
 */
export const savePageDraft = createAsyncThunk<
  void,
  PageJson,
  ThunkApiType
>(
  'childNotebook/savePageDraft',
  async (input, thunkApi) => {
    await service.childNotebook.createPage(input);
    thunkApi.dispatch(uiSlice.actions.setMessage({
      key: 'info',
      content: '保存しました',
    }));
  },
);

/**
 * 既読にする
 */
export const alreadyRead = createAsyncThunk<
  void,
  {
    /** データ */
    data: PageJson,
  },
  ThunkApiType
>(
  'childNotebook/alreadyRead',
  async (input, _thunkApi) => {
    await service.childNotebook.alreadyRead(input.data);
  },
);

/**
 * 健康記録の取得
 */
export const refreshHealthRecords = createAsyncThunk<
  void,
  void,
  ThunkApiType
>(
  'childNotebook/refreshPage',
  async (_, thunkApi) => {
    const {
      date,
      sleep,
      temperature: temperatureState,
      defecation: defecationState,
      milk: milkState,
      meal: mealState,
      drug: drugState,
    } = thunkApi.getState().childNotebook;
    const ydt = toStringDate(newDateFromString(date, 0, -1));
    // 並列に取得・更新するため、各処理をPromiseとして収集
    const tasks: Promise<unknown>[] = [];

    // 体温（当日は必ず、前日は未取得時のみ）
    tasks.push(
      service.temperature.getTemperatureListWithDate(date).then((items) => {
        thunkApi.dispatch(childNotebookSlice.actions.setTemperature({ items, date }));
      }),
    );
    if (!(ydt in temperatureState)) {
      tasks.push(
        service.temperature.getTemperatureListWithDate(ydt).then((yItems) => {
          thunkApi.dispatch(childNotebookSlice.actions.setTemperature(
            { items: yItems, date: ydt },
          ));
        }),
      );
    }

    // 睡眠（当日は未取得時のみ、前日・翌日は未取得時のみ）
    if (!(date in sleep)) {
      tasks.push(
        service.sleep.getSleepListWithDate(date).then((items) => {
          thunkApi.dispatch(childNotebookSlice.actions.setSleep({ items, date }));
        }),
      );
    }
    const tdt = toStringDate(newDateFromString(date, 0, 1));
    if (!(ydt in sleep)) {
      tasks.push(
        service.sleep.getSleepListWithDate(ydt).then((items) => {
          thunkApi.dispatch(childNotebookSlice.actions.setSleep({ items, date: ydt }));
        }),
      );
    }
    if (!(tdt in sleep)) {
      tasks.push(
        service.sleep.getSleepListWithDate(tdt).then((items) => {
          thunkApi.dispatch(childNotebookSlice.actions.setSleep({ items, date: tdt }));
        }),
      );
    }

    // 排泄（当日は必ず、前日は未取得時のみ）
    tasks.push(
      service.defecation.getDefecationListWithDate(date).then((items) => {
        thunkApi.dispatch(childNotebookSlice.actions.setDefecation({ items, date }));
      }),
    );
    if (!(ydt in defecationState)) {
      tasks.push(
        service.defecation.getDefecationListWithDate(ydt).then((yItems) => {
          thunkApi.dispatch(childNotebookSlice.actions.setDefecation({ items: yItems, date: ydt }));
        }),
      );
    }

    // ミルク（当日は必ず、前日は未取得時のみ）
    tasks.push(
      service.milk.getMilkListWithDate(date).then((items) => {
        thunkApi.dispatch(childNotebookSlice.actions.setMilk({ items, date }));
      }),
    );
    if (!(ydt in milkState)) {
      tasks.push(
        service.milk.getMilkListWithDate(ydt).then((yItems) => {
          thunkApi.dispatch(childNotebookSlice.actions.setMilk({ items: yItems, date: ydt }));
        }),
      );
    }

    // 食事（当日は必ず、前日は未取得時のみ）
    tasks.push(
      service.meal.getMealListWithDate(date).then((items) => {
        thunkApi.dispatch(childNotebookSlice.actions.setMeal({ items, date }));
      }),
    );
    if (!(ydt in mealState)) {
      tasks.push(
        service.meal.getMealListWithDate(ydt).then((yItems) => {
          thunkApi.dispatch(childNotebookSlice.actions.setMeal({ items: yItems, date: ydt }));
        }),
      );
    }

    // 薬（当日は必ず、前日は未取得時のみ）
    tasks.push(
      service.drug.getDrugListWithDate(date).then((items) => {
        thunkApi.dispatch(childNotebookSlice.actions.setDrug({ items, date }));
      }),
    );
    if (!(ydt in drugState)) {
      tasks.push(
        service.drug.getDrugListWithDate(ydt).then((yItems) => {
          thunkApi.dispatch(childNotebookSlice.actions.setDrug({ items: yItems, date: ydt }));
        }),
      );
    }

    // すべての並列処理が完了するまで待機
    await Promise.all(tasks);
  },
);

/**
 * 体温削除
 */
export const deleteTemperature = createAsyncThunk<
  void,
  {
    /** データ */
    data: TemperatureJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'temperature/delete',
  async (input, _thunkApi) => {
    await service.temperature.deleteTemperature(input.data);
    if (input.facilityIds.length > 0) {
      const promises = input.facilityIds.map(
        (v) => service.temperatureNursery.removeNurseryTemperature({
          data: input.data.data,
          pub: {
            sendId: input.data.pub.sendId,
            replyId: v,
            data: { ...input.data.pub.data },
          },
        }),
      );
      await Promise.all(promises);
    }
  },
);

/**
 * 睡眠削除
 */
export const deleteSleep = createAsyncThunk<
  void,
  {
    /** データ */
    data: SleepJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'sleep/delete',
  async (input, _thunkApi) => {
    await service.sleep.deleteSleep(input.data);
    if (input.facilityIds.length > 0) {
      const promises = input.facilityIds.map(
        (v) => service.sleepNursery.removeNurserySleep({
          data: input.data.data,
          pub: {
            sendId: input.data.pub.sendId,
            replyId: v,
            data: { ...input.data.pub.data },
          },
        }),
      );
      await Promise.all(promises);
    }
  },
);

/**
 * 排泄削除
 */
export const deleteDefecation = createAsyncThunk<
  void,
  {
    /** データ */
    data: DefecationJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'defecation/delete',
  async (input, _thunkApi) => {
    await service.defecation.deleteDefecation(input.data);
    if (input.facilityIds.length > 0) {
      const promises = input.facilityIds.map(
        (v) => service.defecationNursery.removeNurseryDefecation({
          data: input.data.data,
          pub: {
            sendId: input.data.pub.sendId,
            replyId: v,
            data: { ...input.data.pub.data },
          },
        }),
      );
      await Promise.all(promises);
    }
  },
);

/**
 * ミルク削除
 */
export const deleteMilk = createAsyncThunk<
  void,
  {
    /** データ */
    data: MilkJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'milk/delete',
  async (input, _thunkApi) => {
    await service.milk.deleteMilk(input.data);
    if (input.facilityIds.length > 0) {
      const promises = input.facilityIds.map(
        (v) => service.milkNursery.removeNurseryMilk({
          data: input.data.data,
          pub: {
            sendId: input.data.pub.sendId,
            replyId: v,
            data: { ...input.data.pub.data },
          },
        }),
      );
      await Promise.all(promises);
    }
  },
);

/**
 * 食事削除
 */
export const deleteMeal = createAsyncThunk<
  void,
  {
    /** データ */
    data: MealJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'meal/delete',
  async (input, _thunkApi) => {
    await service.meal.deleteMeal(input.data);
    if (input.facilityIds.length > 0) {
      const promises = input.facilityIds.map(
        (v) => service.mealNursery.removeNurseryMeal({
          data: input.data.data,
          pub: {
            sendId: input.data.pub.sendId,
            replyId: v,
            data: { ...input.data.pub.data },
          },
        }),
      );
      await Promise.all(promises);
    }
  },
);

/**
 * 薬削除
 */
export const deleteDrug = createAsyncThunk<
  void,
  {
    /** データ */
    data: DrugJson,
    /** 施設IDリスト */
    facilityIds: string[],
  },
  ThunkApiType
>(
  'drug/delete',
  async (input, _thunkApi) => {
    await service.drug.deleteDrug(input.data);
    if (input.facilityIds.length > 0) {
      const promises = input.facilityIds.map(
        (v) => service.drugNursery.removeNurseryDrug({
          data: input.data.data,
          pub: {
            sendId: input.data.pub.sendId,
            replyId: v,
            data: { ...input.data.pub.data },
          },
        }),
      );
      await Promise.all(promises);
    }
  },
);
